import {
  EntryTypeCover,
  EntryTypeFree,
  EntryTypeFreeRSVP,
  EntryTypeRSVP,
  EntryTypeTicketed,
  EventFormatClass,
  EventFormatMarket,
  EventFormatNetworking,
  EventFormatParty,
  EventFormatPerformance,
  EventFormatPopup,
  EventFormatSports,
  type EntryType,
  type EventFormat,
  type DiscoveryEventDetail,
  type OrganizationFeatureAccess,
  type OrganizationPlanFeature,
  type EventTagCatalogGroup,
} from '@/lib/api/apiTypes';

export const DEACTIVATED_EVENT_TAG_SLUGS = new Set([
  'casual',
  'business_casual',
  'formal',
  'costume',
  'byob',
  'outdoors',
  'indoors',
  'smoking_allowed',
  'pet_friendly',
  'kid_friendly',
  'free_entry',
]);

export function appendEventFeedFilters(
  params: URLSearchParams,
  entryType?: EntryType | null,
  attributes: string[] = []
) {
  if (entryType) {
    params.set('entry_type', entryType);
  }
  if (attributes.length > 0) {
    params.set('attributes', attributes.join(','));
  }
}

export function getEntryTypeLabel(value?: EntryType | null) {
  if (!value) {
    return '';
  }
  switch (value) {
    case EntryTypeCover:
      return 'Cover Charge';
    case EntryTypeTicketed:
      return 'Ticketed';
    case EntryTypeRSVP:
      return 'RSVP Required';
    case EntryTypeFreeRSVP:
      return 'Free · RSVP';
    case EntryTypeFree:
      return 'Free';
    default:
      return '';
  }
}

function getFirstCoverNoteLine(coverNotes?: string | null) {
  const [firstLine] = (coverNotes ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  return firstLine ?? null;
}

export function getEntryTypeBadgeLabel(
  value?: EntryType | null,
  coverPrice?: number | null,
  coverNotes?: string | null,
  compact = false
) {
  if (!value) {
    return '';
  }
  if (value === EntryTypeCover && typeof coverPrice === 'number') {
    return `Cover $${formatPrice(coverPrice)}`;
  }
  if (value === EntryTypeCover) {
    if (!compact) {
      return getFirstCoverNoteLine(coverNotes) ?? 'Cover Charge';
    }
    return 'Cover';
  }
  if (value === EntryTypeTicketed && typeof coverPrice === 'number') {
    return `From $${formatPrice(coverPrice)}`;
  }

  return getEntryTypeLabel(value);
}

export function getFeedEntryBadgeLabel(
  value?: EntryType | null,
  coverPrice?: number | null,
  coverNotes?: string | null
) {
  if (!value) {
    return null;
  }
  return getEntryTypeBadgeLabel(value, coverPrice, coverNotes, true);
}

type EntryDisplaySource = {
  entry_type?: EntryType | null;
  cover_price?: number | null;
  cover_notes?: string | null;
  is_free?: boolean | null;
  price?: string | null;
};

function parseLegacyCoverPrice(price?: string | null) {
  const match = price?.trim().match(/([0-9]+(?:\.[0-9]+)?)/);
  if (!match) {
    return null;
  }
  const parsed = Number.parseFloat(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
}

function resolveLegacyEntryDetails(source: EntryDisplaySource) {
  const legacyPrice = source.price?.trim() ?? '';
  if (
    source.is_free === true ||
    legacyPrice === '' ||
    legacyPrice.toLowerCase() === 'free' ||
    legacyPrice === '0'
  ) {
    return {
      entryType: EntryTypeFree as EntryType,
      coverPrice: null as number | null,
      coverNotes: null as string | null,
    };
  }

  const parsedCoverPrice = parseLegacyCoverPrice(legacyPrice);
  return {
    entryType: EntryTypeCover as EntryType,
    coverPrice: parsedCoverPrice,
    coverNotes: parsedCoverPrice == null ? legacyPrice : null,
  };
}

export function resolveEventEntryDetails(source: EntryDisplaySource) {
  // Older records can disagree with entry_type; paid legacy fields stay authoritative over stale free flags.
  const legacy = resolveLegacyEntryDetails(source);
  const entryType = source.entry_type ?? null;
  const coverPrice = source.cover_price ?? null;
  const coverNotes = source.cover_notes ?? null;

  if (!entryType) {
    return legacy;
  }

  if (entryType === EntryTypeFree && source.is_free === false && (source.price?.trim() ?? '') !== '') {
    return legacy;
  }

  if (
    (entryType === EntryTypeCover || entryType === EntryTypeTicketed) &&
    coverPrice == null &&
    coverNotes == null &&
    legacy.entryType === EntryTypeCover
  ) {
    return {
      entryType,
      coverPrice: legacy.coverPrice,
      coverNotes: legacy.coverNotes,
    };
  }

  return { entryType, coverPrice, coverNotes };
}

export function getResolvedFeedEntryBadgeLabel(source: EntryDisplaySource) {
  const resolved = resolveEventEntryDetails(source);
  return getFeedEntryBadgeLabel(resolved.entryType, resolved.coverPrice, resolved.coverNotes);
}

export function getEntryPriceLabel(entryType?: EntryType | null) {
  if (entryType === EntryTypeCover) {
    return 'Cover charge';
  }
  if (entryType === EntryTypeTicketed) {
    return 'Ticket price from';
  }
  return '';
}

export function getEntryPriceValue(entryType?: EntryType | null, coverPrice?: number | null) {
  if (
    (entryType === EntryTypeCover || entryType === EntryTypeTicketed) &&
    typeof coverPrice === 'number'
  ) {
    return `$${formatPrice(coverPrice)}`;
  }
  return '';
}

export function shouldShowPrimaryTicketCTA(
  entryType?: EntryType | null,
  ticketURL?: string | null
) {
  return entryType === EntryTypeTicketed && Boolean(ticketURL?.trim());
}

export function shouldShowSecondaryEventWebsite(
  entryType?: EntryType | null,
  eventURL?: string | null
) {
  return entryType !== EntryTypeTicketed && Boolean(eventURL?.trim());
}

export function isTicketedEventWithURL(event: {
  entry_type?: EntryType | null;
  ticket_url?: string | null;
}) {
  return event.entry_type === EntryTypeTicketed && Boolean(event.ticket_url?.trim());
}

export function normalizeExternalHTTPURL(value?: string | null) {
  try {
    const url = new URL(value?.trim() ?? '');
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

export type EventRSVPState = 'default' | 'going' | 'rsvp_full';

type EventRSVPLabelSource = Pick<
  DiscoveryEventDetail,
  'viewer_rsvp_status'
> & {
  entry_type?: EntryType | null;
  is_rsvp_full?: boolean | null;
  is_at_capacity?: boolean | null;
};

export function getEventRSVPState(
  event: EventRSVPLabelSource
): EventRSVPState {
  if (event.viewer_rsvp_status === 'going') {
    return 'going';
  }
  if (event.is_rsvp_full === true) {
    return 'rsvp_full';
  }
  return 'default';
}

export function getEventRSVPButtonLabel(
  event: EventRSVPLabelSource
) {
  switch (getEventRSVPState(event)) {
    case 'going':
      return getCommittedEventActionLabel(event.entry_type);
    case 'rsvp_full':
      return 'RSVP Full';
    default:
      return getDefaultEventActionLabel(event.entry_type);
  }
}

function getDefaultEventActionLabel(entryType?: EntryType | null) {
  switch (entryType) {
    case EntryTypeTicketed:
      return 'Buy Tickets';
    case EntryTypeRSVP:
    case EntryTypeFreeRSVP:
      return 'Reserve Spot';
    case EntryTypeCover:
    case EntryTypeFree:
    default:
      return "I'm Going";
  }
}

function getCommittedEventActionLabel(entryType?: EntryType | null) {
  switch (entryType) {
    case EntryTypeRSVP:
    case EntryTypeFreeRSVP:
      return 'Spot Reserved ✓';
    case EntryTypeTicketed:
    case EntryTypeCover:
    case EntryTypeFree:
    default:
      return "You're Going ✓";
  }
}

export function isEventRSVPDisabled(
  event: Pick<
    DiscoveryEventDetail,
    'viewer_rsvp_status' | 'is_rsvp_full' | 'is_at_capacity'
  >
) {
  const state = getEventRSVPState(event);
  return state === 'rsvp_full';
}

export function getEventLimitNotice(
  event: Pick<
    DiscoveryEventDetail,
    'viewer_rsvp_status' | 'rsvp_count' | 'rsvp_limit' | 'capacity' | 'is_rsvp_full' | 'is_at_capacity'
  >
) {
  if (event.viewer_rsvp_status === 'going') {
    return null;
  }
  if (event.is_rsvp_full) {
    return 'RSVP Full';
  }
  if (typeof event.rsvp_limit === 'number') {
    const remaining = event.rsvp_limit - event.rsvp_count;
    if (remaining > 0 && remaining <= 10) {
      return `${remaining} spots left`;
    }
  }
  return null;
}

export function getEventFormatLabel(value?: EventFormat | null) {
  switch (value) {
    case EventFormatPerformance:
      return 'Performance';
    case EventFormatClass:
      return 'Class / Workshop';
    case EventFormatMarket:
      return 'Market';
    case EventFormatSports:
      return 'Sports';
    case EventFormatPopup:
      return 'Pop-up';
    case EventFormatParty:
      return 'Party';
    case EventFormatNetworking:
      return 'Networking';
    default:
      return value ? humanizeEventValue(value) : '';
  }
}

function formatPrice(value: number) {
  return value % 1 === 0 ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}

function humanizeEventValue(value: string) {
  return value
    .split(/[_-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatLastEntryLabel(value?: string | null) {
  if (!value) {
    return '';
  }
  return `Last entry: ${new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value))}`;
}

export function isDressCodePresetSelected(currentValue: string, presetLabel: string) {
  return currentValue.trim().toLowerCase() === presetLabel.trim().toLowerCase();
}

export function getNextDressCodeFromPreset(currentValue: string, presetLabel: string) {
  return isDressCodePresetSelected(currentValue, presetLabel) ? '' : presetLabel;
}

export function filterSelectableEventTags(tags: string[]) {
  return tags.filter((tag) => !DEACTIVATED_EVENT_TAG_SLUGS.has(tag.trim().toLowerCase()));
}

export function normalizeTagsToFetchedCatalog(
  tags: string[],
  groups?: EventTagCatalogGroup[] | null,
  cachedGroups?: EventTagCatalogGroup[] | null
) {
  const activeGroups =
    groups && groups.length > 0 ? groups : cachedGroups && cachedGroups.length > 0 ? cachedGroups : null;
  if (!activeGroups) {
    return [];
  }

  const allowed = new Set(activeGroups.flatMap((group) => group.tags.map((tag) => tag.slug)));
  return Array.from(
    new Set(
      tags
        .filter((tag): tag is string => typeof tag === 'string')
        .map((tag) => tag.trim().toLowerCase())
        .filter((tag) => tag.length > 0 && allowed.has(tag))
    )
  ).slice(0, 5);
}

export function hasIncludedOrganizationFeature(
  featureAccess: OrganizationFeatureAccess[] | null | undefined,
  feature: OrganizationPlanFeature
) {
  return featureAccess?.some((access) => access.feature === feature && access.included) ?? false;
}
